import { Spinner, SuccessCheck } from './feedback';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { Job } from '../api/types';
import { formatActivityTime, formatDuration, jobKindLabel, jobStatusInfo, stepLabel, viaLabel } from '../format';
import { Badge, Empty } from './ui';
import { ActivityFilters, type ActivityFilterValues } from './ActivityFilters';
import { ExpandableRow } from './ExpandableRow';
import { JobDetails } from './JobDetails';
import { jobPercent } from './JobProgress';

function JobRow({ job, showServer, defaultOpen }: { job: Job; showServer: boolean; defaultOpen: boolean }) {
  const status = jobStatusInfo[job.status] ?? { label: job.status, tone: 'muted' as const };
  const errors = [
    job.error,
    ...job.steps.map((step) => step.error && `${stepLabel[step.name] ?? step.name}: ${step.error}`),
  ].filter((error): error is string => Boolean(error));
  return (
    <ExpandableRow
      label={`ステップの詳細: ${jobKindLabel[job.kind] ?? job.kind}（${job.id}）`}
      className={job.status === 'failed' ? 'activity-failed' : ''}
      defaultOpen={defaultOpen}
      columns={[
        <time className="small activity-nowrap" dateTime={job.createdAt}>
          {formatActivityTime(job.createdAt)}
        </time>,
        ...(showServer ? [<Link to={`/servers/${encodeURIComponent(job.serverId)}`}>{job.serverId}</Link>] : []),
        <span>{jobKindLabel[job.kind] ?? job.kind}</span>,
        <div className="activity-status">
          <Badge tone={status.tone}>
            {(job.status === 'running' || job.status === 'queued') && <Spinner />}
            {job.status === 'succeeded' && <SuccessCheck />}
            {status.label}
            {(job.status === 'running' || job.status === 'queued') && ` ${jobPercent(job)}%`}
          </Badge>
          {errors.map((error, index) => (
            <span key={index} className="activity-error small" title={error}>
              {error.split(/\r?\n/)[0]}
            </span>
          ))}
        </div>,
        <span className="small activity-nowrap">{formatDuration(job.startedAt, job.finishedAt)}</span>,
        <span className="small">
          {job.requestedBy} <span className="tag">{job.via === 'mcp' ? 'AI が実行' : viaLabel[job.via]}</span>
        </span>,
      ]}
    >
      <JobDetails job={job} />
    </ExpandableRow>
  );
}

export function JobTable({ jobs, showServer = true }: { jobs: Job[]; showServer?: boolean }) {
  const [filters, setFilters] = useState<ActivityFilterValues>({ server: '', status: '', kind: '' });
  const [limit, setLimit] = useState(50);
  const [params] = useSearchParams();
  const filtered = jobs.filter(
    (job) =>
      (!showServer || !filters.server || job.serverId === filters.server) &&
      (!filters.kind || job.kind === filters.kind) &&
      (!filters.status ||
        (filters.status === 'running' ? ['queued', 'running'].includes(job.status) : job.status === filters.status)),
  );
  if (jobs.length === 0) return <Empty>ジョブはまだありません。</Empty>;
  return (
    <div className="stack activity-list">
      <ActivityFilters
        values={filters}
        onChange={(values) => {
          setFilters(values);
          setLimit(50);
        }}
        servers={showServer ? [...new Set(jobs.map((job) => job.serverId))].sort() : undefined}
        kinds={[...new Set(jobs.map((job) => job.kind))]
          .sort()
          .map((kind) => ({ value: kind, label: jobKindLabel[kind] ?? kind }))}
        statuses={['running', 'succeeded', 'failed', 'cancelled'].map((status) => ({
          value: status,
          label: jobStatusInfo[status]?.label ?? status,
        }))}
      />
      {filtered.length === 0 ? (
        <Empty>条件に合うジョブはありません。</Empty>
      ) : (
        <div className="table-wrap">
          <table className="activity-table">
            <thead>
              <tr>
                <th aria-label="詳細" />
                <th>日時</th>
                {showServer && <th>サーバー</th>}
                <th>操作</th>
                <th>状態・エラー</th>
                <th>所要</th>
                <th>依頼者</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, limit).map((job) => (
                <JobRow key={job.id} job={job} showServer={showServer} defaultOpen={params.get('jobId') === job.id} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="row activity-pagination">
        <span className="small faint">
          {Math.min(limit, filtered.length)} / {filtered.length} 件
        </span>
        {filtered.length > limit && (
          <button className="btn" onClick={() => setLimit(limit + 50)}>
            もっと見る
          </button>
        )}
      </div>
    </div>
  );
}
